import React, { useState } from "react";
import EACheckbox from "../../shared/Checkbox/EACheckbox";
import EAButton from "../../shared/Buttons/EAButton";
import useFacility from "../../../../utils/hooks/useFacility";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import { useTranslation } from "react-i18next";

interface IProps {
  onAllowMailingChange?: (value: boolean) => void;
  checked?: boolean;
}

const KRK_FACILITY_IDS = ["1241", "1243"];

// Zapis do newslettera (sendInvite) NIE jest wysyłany stąd — samo zaznaczenie
// checkboxa tylko ustawia wartość w formularzu (setValue("allowMailing", ...) w
// ConsentInputs). Realny request idzie z handleRegistration w FinalizeNoLogin/
// FinalizeWithLogin, PRZED saveReservation/acceptTransaction — inaczej request
// (odpalany wcześniej z opóźnieniem) przegrywał wyścig z przekierowaniem do
// płatności i nigdy nie docierał do serwera.
const NewsletterCheckbox: React.FC<IProps> = ({ onAllowMailingChange, checked: externalChecked }) => {
  const [localChecked, setLocalChecked] = useState(false);
  const isChecked = externalChecked !== undefined ? externalChecked : localChecked;
  const [openText, setOpenText] = useState(false);
  const { t } = useTranslation();
  const facilityIds = useFacility();
  const newsletterActive = useSelector((state: State) => state.config.configuration?.newsLetterActive);
  const privatePolicy = useSelector((state: State) => state.config.configuration?.regulaminList[0]?.file);

  const isKRK = KRK_FACILITY_IDS.includes(facilityIds);

  const standardLabel = t(
    "Zaznaczając to pole, wyrażam zgodę na otrzymywanie newslettera drogą mailową. Rozumiem i akceptuję warunki <a target='_blank' href='{{privatePolicy}}'>polityki prywatności</a> dotyczące przetwarzania moich danych osobowych.",
    { privatePolicy }
  );

  const krkLabel = t(
    "Wyrażam zgodę na przetwarzanie danych osobowych w celu przesyłania przez Park Wodny w Krakowie S.A. bieżących informacji o swoich usługach, ofertach handlowych - na udostępniony adres poczty elektronicznej Klienta."
  );

  const handleChange = (value: boolean) => {
    setLocalChecked(value);
    if (onAllowMailingChange) {
      onAllowMailingChange(value);
    }
  };

  if (!newsletterActive) return null;

  return (
    <div style={{ marginTop: "16px" }}>
      <div className="flex-start-center">
        <h5>{t("dolacz_do_newslettera")}</h5>
      </div>

      <EACheckbox
        name="newsletter"
        checked={isChecked}
        onChange={handleChange}
        label={
          isKRK ? (
            <span>{krkLabel}</span>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: standardLabel }} />
          )
        }
      />

      {isKRK && (
        <>
          <EAButton
            title={openText ? t("gotowe_schowaj") : t("przeczytaj_calosc")}
            onClick={() => setOpenText((prev) => !prev)}
            variant="text"
            noMargins
            color={openText ? "secondary" : "primary"}
          />

          {openText && (
            <p className="newsletter__description">
              Wyrażam dobrowolną zgodę na przetwarzanie moich danych osobowych przez Park Wodny w Krakowie S.A. z
              siedzibą w Krakowie, ul. Dobrego Pasterza 126, 31-416 Kraków, tel.: 12 61 63 190 e-mail:
              biuro@parkwodny.pl, jako Administratora Danych Osobowych (dalej: ADO), dane kontaktowe do IOD :
              inspektor@parkwodny.pl. 1. Dane osobowe będą przetwarzane na podstawie zgody wyłącznie przez czas trwania
              udzielonej zgody. 2. Dane osobowe mogą być udostępnione przez ADO: pracownikom/współpracownikom,
              kontrahentom - umocowanym do ich przetwarzania przy działaniach, dot. celu określonego w zgodzie,
              podmiotom świadczącym na rzecz ADO usługi np.: techniczne i organizacyjne, w szczególności dostawcom usług
              teleinformatycznych, nstytucjom działającym i uprawnionym z mocy prawa. 3. Posiada Pan/Pani prawo dostępu
              do treści swoich danych oraz prawo ich sprostowania, jeśli są nieprawidłowe. 4. Przysługuje Panu/Pani
              prawo do zapomnienia, ograniczenia przetwarzania, prawo wniesienia sprzeciwu wobec przetwarzania danych
              oraz prawo wycofania w każdym czasie zgody na przetwarzanie danych. 5. Cofnięcie zgody następuje poprzez
              formularz.albo drogą pocztową na adres ADO wskazany wyżej lub poprzez złożenie wniosku w formie papierowej
              w punkcie informacji (przy kasach do wejścia na halę basenową w Parku Wodnym w Krakowie S.A.). Wycofanie
              zgody spowoduje natychmiastowe zaprzestanie przetwarzania Pani/Pana danych osobowych przez ADO. Wycofanie
              zgody nie wpływa na zgodność z prawem przetwarzania, które miało miejsce do czasu cofnięcia. 6. ADO nie
              zamierza przekazywać Pana/Pani danych osobowych do odbiorców zlokalizowanych poza Europejskim Obszarem
              Gospodarczym. 7. Nie będzie Pan/Pani podlegać decyzjom podejmowanym w sposób zautomatyzowany (bez udziału
              człowieka), a Pana/Pani dane osobowe nie będą profilowane, chyba, że wyda Pan/ Pani zgodę w tym zakresie
              ADO w celu zbudowania adekwatnej oferty usług dla Pana/Pani, w tym pakietu promocji 8. Ma Pan/Pani prawo
              wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych, gdy uzna Pan/Pani, że przetwarzanie danych
              osobowych przez ADO narusza przepisy obowiązującego prawa, w szczególności Rozporządzenia Parlamentu
              Europejskiego i Rady (UE) 2016/679 z dnia 27.04.2016 r. w sprawie ochrony osób fizycznych w związku z
              przetwarzaniem danych osobowych i w sprawie swobodnego przepływu takich danych oraz uchylenia dyrektywy
              95/46/WE (dalej jako: „RODO"). ADO zapewnia środki ochrony danych osobowych zgodnie z obowiązującymi
              przepisami, w szczególności zgodnie z RODO.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default NewsletterCheckbox;
